import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import * as yup from "yup";

import {
  AuthCard,
  AuthContainer,
  ErrorMessage,
  FormSection,
  HeaderSection,
  InputContainer,
  InputGroup,
  PasswordInputContainer,
  PasswordToggle,
  PrimaryButton,
  ServerErrorAlert,
  StyledInput,
  SuccessMessage,
  Title,
} from "../shared/authStyles";
import {
  EyeIcon,
  EyeSlashIcon,
  TickCircleIcon,
} from "../shared/icons/AuthIcons";
import { AnimatePresence, motion } from "../shared/motionUtils";

// Validation schema for reset password
const resetPasswordSchema = yup.object({
  password: yup
    .string()
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .required("Password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "Passwords must match")
    .required("Please confirm your password"),
});

const FormSuccessMessage = styled(motion.div)`
  color: #1e2223;
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  margin-top: 4px;
  padding: 8px 12px;
  background: #efe;
  border: 1px solid #cfc;
  border-radius: 4px;
`;

const ResetPassword = ({ match, reset, resetPassword, resetRedirectReset }) => {
  const history = useHistory();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [confirmationKey, setConfirmationKey] = useState("");
  const passwordRef = useRef(null);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const passwordValue = watch("password");
  const confirmPasswordValue = watch("confirmPassword");
  const passwordsMatch =
    passwordValue &&
    confirmPasswordValue &&
    passwordValue === confirmPasswordValue &&
    !errors.confirmPassword;

  useEffect(() => {
    let token = localStorage.getItem("access_token");
    if (token != null) {
      history.push("/user/dashboard");
    }

    const {
      params: { confirmation_key },
    } = match;
    setConfirmationKey(confirmation_key);
    return () => {
      document.body.classList.toggle("login-page");
    };
  }, [history, match]);

  // Auto-focus password field on component mount
  useEffect(() => {
    if (passwordRef.current) {
      passwordRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (resetPassword?.redirect === true) {
      resetRedirectReset();
      history.push("/auth/login");
    }
  }, [resetPassword, resetRedirectReset, history]);

  const onSubmit = useCallback(
    data => {
      setServerError(null);
      setSuccessMessage(null);

      const payload = {
        confirmation_key: confirmationKey,
        password: data.password,
      };
      reset(payload);
    },
    [reset, confirmationKey],
  );

  const toggleShowPassword = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const toggleShowConfirmPassword = useCallback(() => {
    setShowConfirmPassword(prev => !prev);
  }, []);

  return (
    <AuthContainer>
      <AuthCard
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      >
        <HeaderSection
          marginBottom="0"
          alignItems="flex-start"
          justifyContent="flex-start"
        >
          <Title
            margin="0"
            fontSize="32px"
            lineHeight="48px"
            color="#000e2a"
            fontWeight="700"
            textAlign="left"
          >
            Set new password
          </Title>
        </HeaderSection>

        <FormSection
          as="form"
          onSubmit={handleSubmit(onSubmit)}
          marginTop="32px"
        >
          <InputContainer>
            <InputGroup>
              <PasswordInputContainer>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <StyledInput
                      {...field}
                      ref={passwordRef}
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      autoComplete="new-password"
                      hasError={!!errors.password}
                    />
                  )}
                />
                <PasswordToggle type="button" onClick={toggleShowPassword}>
                  {showPassword ? (
                    <EyeSlashIcon size="24px" color="#6b7280" />
                  ) : (
                    <EyeIcon size="24px" />
                  )}
                </PasswordToggle>
              </PasswordInputContainer>
              <AnimatePresence>
                {errors.password && (
                  <ErrorMessage
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {errors.password.message}
                  </ErrorMessage>
                )}
              </AnimatePresence>
            </InputGroup>

            <InputGroup>
              <PasswordInputContainer>
                <Controller
                  name="confirmPassword"
                  control={control}
                  render={({ field }) => (
                    <StyledInput
                      {...field}
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Reenter new password"
                      autoComplete="new-password"
                      hasError={!!errors.confirmPassword}
                      hasSuccess={passwordsMatch}
                    />
                  )}
                />
                <PasswordToggle
                  type="button"
                  onClick={toggleShowConfirmPassword}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon size="24px" color="#6b7280" />
                  ) : (
                    <EyeIcon size="24px" />
                  )}
                </PasswordToggle>
              </PasswordInputContainer>
              <AnimatePresence>
                {errors.confirmPassword ? (
                  <ErrorMessage
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {errors.confirmPassword.message}
                  </ErrorMessage>
                ) : (
                  passwordsMatch && (
                    <SuccessMessage
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      <TickCircleIcon size="16px" color="white" />
                      Password Matched
                    </SuccessMessage>
                  )
                )}
              </AnimatePresence>
            </InputGroup>
          </InputContainer>

          <AnimatePresence>
            {serverError && (
              <ServerErrorAlert
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {serverError}
              </ServerErrorAlert>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {successMessage && (
              <FormSuccessMessage
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {successMessage}
              </FormSuccessMessage>
            )}
          </AnimatePresence>

          <PrimaryButton
            type="submit"
            disabled={resetPassword?.isLoading || isSubmitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {resetPassword?.isLoading ? "Saving..." : "Confirm New Password"}
          </PrimaryButton>
        </FormSection>
      </AuthCard>
    </AuthContainer>
  );
};

export default ResetPassword;
